import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp, FaCodeBranch, FaEyeSlash, FaLink } from "react-icons/fa";
import http from "../../api/apiClient";
import { formatDutchDate } from "../../utils/dutchDate";
import { euro, type Allocation, type Receipt } from "./contributionsModel";
import { payerLabel, TermSelect } from "./ContributionDetails";
import FamilyPicker, { type FamilyOption } from "./FamilyPicker";
import SplitEditor from "./SplitEditor";

// The work queue of the contributions page: payments from e-Boekhouden still to link, and the ones left out.

/** A parent of the chosen family, for saving the payer's IBAN; the backend never sends account numbers here. */
type IbanParent = { parentId: string; name?: string | null; hasBankAccount: boolean; hasThisIban: boolean };

const WAITING_OPEN_KEY = "lumen.finance.waitingOpen";
/** Rows shown at once in an opened list; a long queue made the whole page slow. */
export const WAITING_PAGE_SIZE = 50;

// Whether the list was left open is remembered per browser; storage may be unavailable (private mode).
const readWaitingOpen = (): boolean => {
  try {
    return window.localStorage.getItem(WAITING_OPEN_KEY) === "1";
  } catch {
    return false;
  }
};
const storeWaitingOpen = (open: boolean) => {
  try {
    window.localStorage.setItem(WAITING_OPEN_KEY, open ? "1" : "0");
  } catch {
    // Not remembered; the list still works.
  }
};

/** Payments from e-Boekhouden that could not be linked automatically. */
export function WaitingList({ receipts, families, suggestions, canManage, onChanged, months = [], totalCount, searching = false }: {
  months?: string[];
  /** All payments to link; set when {@code receipts} is filtered by the search. */
  totalCount?: number;
  /** A search is active: the list opens to show what matches. */
  searching?: boolean;
  receipts: Receipt[];
  families: FamilyOption[];
  suggestions: Record<string, Allocation[]>;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  // Closed by default: rows are only built when the list is open.
  const [open, setOpen] = useState(readWaitingOpen);
  const [shown, setShown] = useState(WAITING_PAGE_SIZE);
  useEffect(() => {
    if (searching) setOpen(true);
  }, [searching]);
  const filtered = totalCount !== undefined && totalCount !== receipts.length;
  const total = receipts.reduce((sum, receipt) => sum + Number(receipt.amount), 0);
  const visible = receipts.slice(0, shown);
  const rest = receipts.length - visible.length;
  const toggle = () => {
    const next = !open;
    setOpen(next);
    storeWaitingOpen(next);
  };
  return (
    <section className="rounded-[24px] border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-400/20 dark:bg-amber-400/5 sm:p-5">
      <h2 className="text-base font-bold text-slate-950 dark:text-white">
        <button type="button" aria-expanded={open} onClick={toggle} className="inline-flex flex-wrap items-center gap-2 text-left">
          {open ? <FaChevronUp aria-hidden="true" className="text-slate-500" /> : <FaChevronDown aria-hidden="true" className="text-slate-500" />}
          <FaLink aria-hidden="true" className="text-amber-600" />
          <span>
            {filtered
              ? t("finance.payments.waiting_title_filtered", { count: receipts.length, total: totalCount, defaultValue: "To link ({{count}} of {{total}})" })
              : t("finance.payments.waiting_title", { count: receipts.length, defaultValue: "To link ({{count}})" })}
          </span>
          <span className="text-sm font-semibold text-slate-500">{euro(total)}</span>
        </button>
      </h2>
      {open && (
        <>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {t("finance.payments.waiting_help", {
              defaultValue: "These payments could not be linked to a family by IBAN. Choose the family; with 'remember' later payments from the same payer are linked automatically.",
            })}
          </p>
          {receipts.length === 0 && (
            <p className="mt-3 text-sm text-slate-500">
              {t("finance.payments.waiting_no_match", { defaultValue: "No payment to link matches the search." })}
            </p>
          )}
          <ul className="mt-3 divide-y divide-amber-200/70 rounded-xl border border-amber-200 bg-white empty:hidden dark:divide-white/10 dark:border-white/10 dark:bg-slate-900">
            {visible.map((receipt) => (
              <WaitingRow key={receipt.id} receipt={receipt} families={families} suggestion={suggestions[receipt.id]}
                canManage={canManage} onChanged={onChanged} months={months} />
            ))}
          </ul>
          {rest > 0 && (
            <button
              type="button"
              onClick={() => setShown((current) => current + WAITING_PAGE_SIZE)}
              className="mt-3 inline-flex h-9 items-center rounded-lg border border-amber-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-amber-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/5"
            >
              {t("finance.payments.waiting_more", { count: Math.min(rest, WAITING_PAGE_SIZE), rest, defaultValue: "Show {{count}} more ({{rest}} not shown)" })}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function WaitingRow({ receipt, families, suggestion, canManage, onChanged, months }: {
  // This project's JSX typings do not add `key` to function components declared like this.
  key?: string;
  months: string[];
  receipt: Receipt;
  families: FamilyOption[];
  suggestion?: Allocation[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  // A payer set to "always ask" opens straight away with the previous division filled in.
  const [open, setOpen] = useState(Boolean(suggestion?.length));
  const [message, setMessage] = useState<string | null>(null);
  const busy = useRef(false);

  const ignore = async () => {
    if (busy.current) return;
    busy.current = true;
    setMessage(null);
    try {
      await http.post(`/finance/receipts/${receipt.id}/ignore`, { ignored: true });
      onChanged();
    } catch {
      setMessage(t("finance.payments.save_failed", { defaultValue: "Saving failed. Please try again." }));
    } finally {
      busy.current = false;
    }
  };

  return (
    <li data-receipt={receipt.id} className="grid gap-2 px-3 py-2.5 text-sm lg:grid-cols-[6rem_6rem_minmax(0,1fr)_auto] lg:items-center">
      <span className="tabular-nums text-slate-600 dark:text-slate-300">{formatDutchDate(receipt.date)}</span>
      <span className="font-bold tabular-nums">{euro(receipt.amount)}</span>
      <span className="min-w-0">
        <span className="block truncate font-semibold text-slate-900 dark:text-white">{payerLabel(t, receipt)}</span>
        <span className="block truncate text-xs text-slate-500" title={receipt.description || ""}>
          {receipt.description || t("finance.payments.no_description", { defaultValue: "No description" })}
        </span>
      </span>
      {canManage ? (
        <span className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 text-sm font-bold text-white hover:bg-indigo-700"
          >
            <FaLink aria-hidden="true" />
            {t("finance.payments.link", { defaultValue: "Link" })}
          </button>
          <button
            type="button"
            onClick={ignore}
            title={t("finance.payments.ignore_help", { defaultValue: "Not a contribution: leave this payment out" })}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
          >
            <FaEyeSlash aria-hidden="true" />
            {t("finance.payments.ignore", { defaultValue: "Leave out" })}
          </button>
        </span>
      ) : <span />}
      {message && <p role="alert" className="text-xs font-semibold text-rose-600 lg:col-span-4">{message}</p>}
      {canManage && open && (
        <div className="lg:col-span-4">
          <LinkPanel receipt={receipt} families={families} suggestion={suggestion} months={months} onCancel={() => setOpen(false)} onDone={onChanged} />
        </div>
      )}
    </li>
  );
}

/**
 * Linking step by step: which family, then for whom (the whole family, one child, or divide over several
 * families), then remember for the payer's next payments.
 */
function LinkPanel({ receipt, families, suggestion, months, onCancel, onDone }: {
  months: string[];
  receipt: Receipt;
  families: FamilyOption[];
  suggestion?: Allocation[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  // A payer can be remembered by its e-Boekhouden relation or, for a bank line without relation, by its IBAN.
  const hasPayer = Boolean(receipt.relationName || receipt.payerIbanMasked);
  const [familyId, setFamilyId] = useState("");
  // "" = whole family, a child id, or "split".
  const [target, setTarget] = useState(suggestion?.length ? "split" : "");
  // Off by default: remembering links every later payment of this payer, so it is a deliberate choice.
  const [remember, setRemember] = useState(false);
  // Off by default: the payer's IBAN is only saved with the parents ticked here, and only when they have no bank account yet.
  const [ibanParents, setIbanParents] = useState<IbanParent[]>([]);
  const [ibanParentIds, setIbanParentIds] = useState<string[]>([]);
  const [ibanParentsFailed, setIbanParentsFailed] = useState(false);
  const [termMonth, setTermMonth] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const family = families.find((option) => option.familyId === familyId);
  const children = family?.children ?? [];

  useEffect(() => {
    setIbanParents([]);
    setIbanParentIds([]);
    setIbanParentsFailed(false);
    if (!familyId || !receipt.payerIbanMasked) return;
    let current = true;
    Promise.resolve(http.get(`/finance/receipts/${receipt.id}/payer-iban/parents`, { params: { familyId } }))
      .then((response) => {
        if (current) setIbanParents(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        // Linking still works; only saving the IBAN is not possible, and that is said instead of hiding the choice.
        if (current) setIbanParentsFailed(true);
      });
    return () => {
      current = false;
    };
  }, [familyId, receipt.id, receipt.payerIbanMasked]);

  const toggleIbanParent = (parentId: string, checked: boolean) =>
    setIbanParentIds((ids) => (checked ? [...ids.filter((id) => id !== parentId), parentId] : ids.filter((id) => id !== parentId)));

  const save = async () => {
    if (!familyId || saving) return;
    setSaving(true);
    setMessage(null);
    try {
      await http.post(`/finance/receipts/${receipt.id}/link`, {
        familyId,
        childId: target || null,
        remember: remember && hasPayer,
        termMonth: termMonth || null,
      });
      if (ibanParentIds.length && receipt.payerIbanMasked) {
        await http.post(`/finance/receipts/${receipt.id}/payer-iban`, { familyId, parentIds: ibanParentIds });
      }
      onDone();
    } catch {
      setMessage(t("finance.payments.save_failed", { defaultValue: "Saving failed. Please try again." }));
    } finally {
      setSaving(false);
    }
  };

  if (target === "split") {
    return (
      <SplitEditor
        receipt={receipt}
        families={families}
        suggestion={suggestion}
        initial={suggestion?.length ? undefined : [{ familyId, childId: null, amount: Number(receipt.amount) }]}
        onCancel={() => (suggestion?.length ? onCancel() : setTarget(""))}
        onDone={onDone}
      />
    );
  }

  const option = "inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm";
  return (
    <div className="grid gap-3 rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 text-sm dark:border-indigo-400/20 dark:bg-indigo-400/5">
      <div className="grid gap-1">
        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
          {t("finance.payments.step_family", { defaultValue: "Which family?" })}
        </span>
        <FamilyPicker
          options={families}
          value={familyId}
          withChildren
          onChange={(value, childId) => {
            setFamilyId(value);
            // Typing a child's name and picking it links straight to that child.
            setTarget(childId || "");
          }}
          label={t("finance.payments.choose_family_for", { amount: euro(receipt.amount), defaultValue: "Family for the payment of {{amount}}" })}
        />
      </div>

      {familyId && (
        <div role="radiogroup" aria-label={t("finance.payments.step_for", { defaultValue: "For whom?" })} className="grid gap-1">
          <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
            {t("finance.payments.step_for", { defaultValue: "For whom?" })}
          </span>
          <div className="flex flex-wrap gap-2">
            <label className={`${option} ${target === "" ? "border-indigo-300 bg-white font-semibold dark:bg-slate-900" : "border-slate-200 dark:border-white/10"}`}>
              <input type="radio" name={`target-${receipt.id}`} checked={target === ""} onChange={() => setTarget("")} />
              {t("finance.payments.whole_family_short", { name: family?.familyName || "", defaultValue: "The whole family {{name}}" })}
            </label>
            {(children.length > 1 || (target !== "" && target !== "split")) && children.map((child) => (
              <label key={child.childId} className={`${option} ${target === child.childId ? "border-indigo-300 bg-white font-semibold dark:bg-slate-900" : "border-slate-200 dark:border-white/10"}`}>
                <input type="radio" name={`target-${receipt.id}`} checked={target === child.childId} onChange={() => setTarget(child.childId)} />
                {child.name}
              </label>
            ))}
            <label className={`${option} border-slate-200 dark:border-white/10`}>
              <input type="radio" name={`target-${receipt.id}`} checked={false} onChange={() => setTarget("split")} />
              <FaCodeBranch aria-hidden="true" className="text-slate-500" />
              {t("finance.payments.split.choice", { defaultValue: "Divide over several families or children" })}
            </label>
          </div>
        </div>
      )}

      {familyId && months.length > 0 && (
        <label className="grid gap-1 justify-self-start text-xs font-bold text-slate-600 dark:text-slate-300">
          {t("finance.payments.term_question", { defaultValue: "For which term?" })}
          <TermSelect months={months} value={termMonth} automaticMonth={receipt.termMonth} onChange={setTermMonth}
            label={t("finance.payments.term_question", { defaultValue: "For which term?" })} />
        </label>
      )}

      {familyId && receipt.payerIbanMasked && ibanParentsFailed && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          {t("finance.payments.iban_parents_failed", { iban: receipt.payerIbanMasked, defaultValue: "The parents of this family could not be loaded, so IBAN {{iban}} cannot be saved here now." })}
        </p>
      )}

      {familyId && receipt.payerIbanMasked && ibanParents.length > 0 && (
        <fieldset className="grid gap-1">
          <legend className="text-xs font-bold text-slate-600 dark:text-slate-300">
            {t("finance.payments.add_iban_to", { iban: receipt.payerIbanMasked, defaultValue: "Save IBAN {{iban}} with parent" })}
          </legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {ibanParents.map((parent, index) => {
              const name = parent.name || t("finance.payments.parent_n", { n: index + 1, defaultValue: "Parent {{n}}" });
              const note = parent.hasThisIban
                ? t("finance.payments.iban_already_known", { defaultValue: "this IBAN is already known" })
                : parent.hasBankAccount
                  ? t("finance.payments.has_bank_account", { defaultValue: "already has a bank account" })
                  : null;
              return (
                <label key={parent.parentId} className={`inline-flex items-center gap-2 text-xs ${parent.hasBankAccount ? "text-slate-400" : "text-slate-700 dark:text-slate-200"}`}>
                  <input type="checkbox" disabled={parent.hasBankAccount}
                    checked={!parent.hasBankAccount && ibanParentIds.includes(parent.parentId)}
                    onChange={(event) => toggleIbanParent(parent.parentId, event.target.checked)} />
                  {name}
                  {note && <span>({note})</span>}
                </label>
              );
            })}
          </div>
        </fieldset>
      )}

      {familyId && (
        <label className={`inline-flex items-center gap-2 text-xs ${hasPayer ? "text-slate-700 dark:text-slate-200" : "text-slate-400"}`}
          title={hasPayer ? undefined : t("finance.payments.remember_unavailable", { defaultValue: "No payer and no IBAN known, so this cannot be remembered." })}>
          <input type="checkbox" checked={remember && hasPayer} disabled={!hasPayer} onChange={(event) => setRemember(event.target.checked)} />
          {t("finance.payments.remember_long", { defaultValue: "Remember: link later payments from this payer the same way" })}
        </label>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={!familyId || saving}
          className="inline-flex h-9 items-center rounded-lg bg-indigo-600 px-3 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-40">
          {t("finance.payments.link_confirm", { defaultValue: "Link payment" })}
        </button>
        <button type="button" onClick={onCancel}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-white dark:border-white/10 dark:text-slate-300">
          {t("common.cancel", { defaultValue: "Cancel" })}
        </button>
      </div>
      {message && <p role="alert" className="text-xs font-semibold text-rose-600">{message}</p>}
    </div>
  );
}

/** Payments that were left out; they can be counted again. */
export function IgnoredList({ receipts, canManage, onChanged }: { receipts: Receipt[]; canManage: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-[24px] border border-slate-200 bg-white px-4 py-3 text-sm dark:border-white/10 dark:bg-slate-900">
      <button type="button" aria-expanded={open} onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-2 font-semibold text-slate-600 dark:text-slate-300">
        {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
        {t("finance.payments.ignored_title", { count: receipts.length, defaultValue: "Left out ({{count}})" })}
      </button>
      {open && (
        <ul className="mt-2 divide-y divide-slate-100 dark:divide-white/10">
          {receipts.map((receipt) => (
            <li key={receipt.id} className="flex flex-wrap items-center gap-3 py-2">
              <span className="w-24 tabular-nums text-slate-500">{formatDutchDate(receipt.date)}</span>
              <span className="w-20 font-semibold tabular-nums">{euro(receipt.amount)}</span>
              <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">
                {payerLabel(t, receipt)}{receipt.description ? ` · ${receipt.description}` : ""}
              </span>
              {canManage && (
                <button
                  type="button"
                  onClick={async () => {
                    await http.post(`/finance/receipts/${receipt.id}/ignore`, { ignored: false });
                    onChanged();
                  }}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300"
                >
                  {t("finance.payments.count_again", { defaultValue: "Count again" })}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
